/**
 * Icon SVG Generator
 *
 * Generates SVG markup for preset icons used in logo and watermark.
 * These SVGs are sent to Owl Ops for rendering.
 */

import {
  Briefcase,
  Building2,
  Hammer,
  Wrench,
  Award,
  Leaf,
  Zap,
  Shield,
  type LucideIcon,
} from 'lucide-react';

/**
 * Preset icon definitions - single source of truth for all icon options.
 * Used by LogoPanel, FullDocumentView, and anywhere else preset icons are rendered.
 */
export interface PresetIcon {
  id: string;
  icon: LucideIcon | null; // null for 'initials' which renders text
  name: string;
}

export const PRESET_ICONS: PresetIcon[] = [
  { id: 'initials', icon: null, name: 'Company Initial' },
  { id: 'briefcase', icon: Briefcase, name: 'Business' },
  { id: 'building', icon: Building2, name: 'Corporate' },
  { id: 'hammer', icon: Hammer, name: 'Construction' },
  { id: 'wrench', icon: Wrench, name: 'Services' },
  { id: 'award', icon: Award, name: 'Premium' },
  { id: 'leaf', icon: Leaf, name: 'Eco' },
  { id: 'zap', icon: Zap, name: 'Energy' },
  { id: 'shield', icon: Shield, name: 'Security' },
];

/**
 * Get the Lucide icon component for a preset icon ID.
 */
export function getPresetIconComponent(iconId: string): LucideIcon | null {
  return PRESET_ICONS.find(p => p.id === iconId)?.icon ?? null;
}

/**
 * Extract initials from a business name (up to 2 characters).
 * - "West Andrews" → "WA"
 * - "West Andrews Hotdogs" → "WA" (first two words only)
 * - "West" → "W"
 *
 * @param businessName - The business name to extract initials from
 * @returns 1-2 character initials string
 */
export function getBusinessInitials(businessName: string): string {
  const words = (businessName || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
}

/**
 * Generate an SVG with the business initials (1-2 characters).
 * Used as a placeholder icon when no logo is uploaded.
 *
 * @param businessName - The business name to extract initials from
 * @returns SVG markup string
 */
export function getInitialsSvg(businessName: string): string {
  const initials = getBusinessInitials(businessName);
  // Use currentColor like other icons - the container provides the background
  // Adjust font size based on number of characters
  const fontSize = initials.length === 1 ? 14 : 10;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="none"><text x="12" y="12" dominant-baseline="central" text-anchor="middle" fill="currentColor" font-family="system-ui, -apple-system, sans-serif" font-weight="600" font-size="${fontSize}">${initials}</text></svg>`;
}

// Map of icon IDs to their SVG paths (from Lucide icons)
// Logo icons use currentColor, watermark icons use #888888 for visibility
const ICON_SVGS: Record<string, string> = {
  briefcase: `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="7" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>`,
  building: `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>`,
  hammer: `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 12-8.5 8.5c-.83.83-2.17.83-3 0 0 0 0 0 0 0a2.12 2.12 0 0 1 0-3L12 9"/><path d="M17.64 15 22 10.64"/><path d="m20.91 11.7-1.25-1.25c-.6-.6-.93-1.4-.93-2.25v-.86L16.01 4.6a5.56 5.56 0 0 0-3.94-1.64H9l.92.82A6.18 6.18 0 0 1 12 8.4v1.56l2 2h2.47l2.26 1.91"/></svg>`,
  wrench: `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>`,
  award: `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></svg>`,
  leaf: `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/></svg>`,
  zap: `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>`,
  shield: `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/></svg>`,
};

/**
 * Get the SVG markup for a preset icon.
 *
 * @param iconId - The icon ID (e.g., 'briefcase', 'hammer')
 * @returns SVG markup string, or null if icon not found
 */
export function getIconSvg(iconId: string): string | null {
  return ICON_SVGS[iconId] ?? null;
}

/**
 * Extract icon ID from a url that starts with "icon:".
 *
 * @param url - The logo/watermark url (e.g., "icon:briefcase")
 * @returns The icon ID, or null if not an icon url
 */
export function extractIconId(url: string | null): string | null {
  if (!url?.startsWith('icon:')) return null;
  return url.replace('icon:', '');
}

/**
 * Get the SVG markup for a logo/watermark that uses a preset icon.
 *
 * @param url - The logo/watermark url (e.g., "icon:briefcase" or "icon:initials")
 * @param businessName - Business name (required for "icon:initials")
 * @returns SVG markup string, or null if not an icon or icon not found
 */
export function getIconSvgFromUrl(
  url: string | null,
  businessName?: string,
): string | null {
  const iconId = extractIconId(url);
  if (!iconId) return null;

  // Special case: initials icon generates dynamic SVG
  if (iconId === 'initials') {
    return getInitialsSvg(businessName || 'Company');
  }

  return getIconSvg(iconId);
}

/**
 * Check if a url represents a preset icon.
 */
export function isPresetIcon(url: string | null): boolean {
  return url?.startsWith('icon:') ?? false;
}
